/**
 * Ingredients to Video screen (ingredients tool).
 *
 * Flow: 1 Add 2–4 images ("ingredients") → 2 Describe the scene (+ optional
 * look) → 3 Duration → 4 Quality → Generate. Images are numbered Image 1…4 and
 * the user can refer to them in the scene text ("Image 1 walks through Image 2").
 * The look chips are server-owned wording: the app only sends `options.sceneStyle`.
 * Credits are data (GET /credits/pricing); nothing here hard-codes a price.
 * Defaults are the cheapest option (360p, 5s).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import type {
  CreditPricingConfig,
  JobOptions,
  QualityTier,
  SceneStyle,
  UploadFileMeta,
  VideoDurationSec,
} from '@loomia/shared';

const TOOL = 'ingredients' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;
const MAX_IMAGES = 4;
const MIN_IMAGES = 2;
const PROMPT_MAX = 500;
const PROMPT_MIN = 5;
const TIER_ORDER: QualityTier[] = ['360p', '540p', '720p', '1080p'];
const TIER_HINT: Record<QualityTier, string> = {
  '360p': 'Cheapest',
  '540p': 'Balanced',
  '720p': 'HD',
  '1080p': 'Full HD',
};
const STYLES: ReadonlyArray<{ id: SceneStyle; label: string }> = [
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'fantasy', label: 'Fantasy' },
  { id: 'product_reveal', label: 'Product reveal' },
  { id: 'travel', label: 'Travel' },
];

interface Slot {
  key: number;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function priceOf(pricing: CreditPricingConfig, tier: QualityTier, duration: VideoDurationSec): number | null {
  const v = pricing.perTool[TOOL]?.perTierDuration?.[tier]?.[duration];
  return typeof v === 'number' ? v : null;
}

function errorHtml(message: string): string {
  return `
    <div class="i2v-state">
      ${icon('cloud_off')}
      <p>${escapeHtml(message)}</p>
      <button class="btn btn-secondary" data-act="retry">Try again</button>
    </div>`;
}

export function renderIngredients(root: HTMLElement, query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="i2v"><div class="i2v-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    if (pricingR.status === 'rejected') {
      root.innerHTML = `<div class="i2v">${errorHtml("Couldn't load pricing. Check your connection.")}</div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    const matrix = pricing.perTool[TOOL]?.perTierDuration ?? {};
    const tiers = TIER_ORDER.filter((t) => matrix[t]);
    const durations: VideoDurationSec[] = pricing.perTool[TOOL]?.durations?.length
      ? pricing.perTool[TOOL]!.durations!
      : [5, 10, 15];
    if (tiers.length === 0) {
      root.innerHTML = `<div class="i2v">${errorHtml('Ingredients to Video is not available right now.')}</div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }

    let slots: Slot[] = [];
    let slotSeq = 0;
    let prompt = '';
    let style: SceneStyle | null = null;
    let generating = false;
    let duration: VideoDurationSec = (durations as number[]).includes(Number(query.get('duration')))
      ? (Number(query.get('duration')) as VideoDurationSec)
      : durations[0]!;
    const wantedTier = query.get('quality');
    let tier: QualityTier = (tiers as string[]).includes(wantedTier ?? '') ? (wantedTier as QualityTier) : tiers[0]!;

    const cost = (): number => priceOf(pricing, tier, duration) ?? 0;
    const readyCount = (): number => slots.filter((s) => s.status === 'ready').length;
    const missing = (): string | null => {
      if (slots.some((s) => s.status === 'uploading')) return 'Uploading…';
      if (readyCount() < MIN_IMAGES) return `Add at least ${MIN_IMAGES} images`;
      if (prompt.trim().length < PROMPT_MIN) return 'Describe your scene first';
      return null;
    };

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string, extra = ''): string =>
      `<div class="i2v-step"><span class="i2v-num">${n}</span><h2>${title}</h2>${extra}</div>`;

    const slotHtml = (s: Slot, i: number): string => {
      const label =
        s.status === 'uploading'
          ? `Uploading… ${Math.round(s.progress * 100)}%`
          : s.status === 'error'
            ? escapeHtml(s.error ?? 'Upload failed')
            : '';
      return `
        <div class="ing-slot is-${s.status}" data-key="${s.key}">
          <div class="ing-thumb">
            ${s.previewUrl ? `<img src="${escapeHtml(s.previewUrl)}" alt="Image ${i + 1}" />` : ''}
            <span class="ing-tag">Image ${i + 1}</span>
            <button class="ing-x" data-remove="${s.key}" aria-label="Remove image ${i + 1}">${icon('close')}</button>
            ${s.status === 'uploading' ? `<div class="i2v-bar ing-bar"><span style="width:${Math.round(s.progress * 100)}%"></span></div>` : ''}
          </div>
          ${label ? `<span class="ing-note${s.status === 'error' ? ' is-error' : ''}" data-slot-label>${label}</span>` : ''}
        </div>`;
    };

    const slotsHtml = (): string => `
      <div class="ing-grid">
        ${slots.map(slotHtml).join('')}
        ${
          slots.length < MAX_IMAGES
            ? `<button class="ing-add" data-act="pick" aria-label="Add image">${icon('add')}<span>Add image</span></button>`
            : ''
        }
      </div>
      <p class="i2v-sub">Use clear photos of the people, objects or places you want in your video. Add at least ${MIN_IMAGES}.</p>
      <p class="ing-note-line">${icon('info')}Only use photos of yourself or people who agreed.</p>`;

    const sceneHtml = (): string => `
      <div class="i2v-text">
        <textarea id="ing-prompt" maxlength="${PROMPT_MAX}" rows="4"
          placeholder="Describe what happens. Refer to your images, e.g. “Image 1 walks through Image 2 at sunset.”"
          aria-label="Describe your scene"></textarea>
        <div class="i2v-text-foot">
          <button class="i2v-link" data-act="clear" id="ing-clear" hidden>Clear</button>
          <span class="i2v-count" id="ing-count">0/${PROMPT_MAX}</span>
        </div>
      </div>
      <div class="i2v-chips" role="group" aria-label="Look">
        ${STYLES.map(
          (st) => `<button class="i2v-pchip${st.id === style ? ' selected' : ''}" aria-pressed="${st.id === style}" data-style="${st.id}">${escapeHtml(st.label)}</button>`,
        ).join('')}
      </div>`;

    const durationHtml = (): string => `
      <div class="i2v-opts" role="radiogroup" aria-label="Duration">
        ${durations
          .map(
            (d) => `
          <button class="i2v-opt${d === duration ? ' selected' : ''}" role="radio" aria-checked="${d === duration}" data-duration="${d}">
            <strong>${d}s</strong><span>${icon('toll', { fill: true })} ${priceOf(pricing, tier, d) ?? '–'}</span>
          </button>`,
          )
          .join('')}
      </div>`;

    const qualityHtml = (): string => `
      <div class="i2v-opts" role="radiogroup" aria-label="Quality">
        ${tiers
          .map(
            (t) => `
          <button class="i2v-opt${t === tier ? ' selected' : ''}" role="radio" aria-checked="${t === tier}" data-tier="${t}">
            <strong>${t}</strong><small>${TIER_HINT[t]}</small><span>${icon('toll', { fill: true })} ${priceOf(pricing, t, duration) ?? '–'}</span>
          </button>`,
          )
          .join('')}
      </div>
      <p class="i2v-sub">Higher quality = sharper video</p>`;

    const footerHtml = (): string => {
      const c = cost();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Generate Video ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <div class="i2v-total${short > 0 ? ' is-short' : ''}">
          <span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong>
        </div>
        ${short > 0 ? `<p class="i2v-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}
        <p class="i2v-refund">Failed runs are refunded automatically</p>`;
    };

    const paint = (): void => {
      root.innerHTML = `
        <div class="i2v">
          <header class="i2v-head">
            <button class="i2v-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <h1>Ingredients to Video</h1>
            <a class="i2v-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          <p class="i2v-sub">Bring multiple images together in one AI video.</p>
          ${step(1, 'Add ingredients', `<span class="i2v-sub">Up to ${MAX_IMAGES} images</span>`)}
          ${slotsHtml()}
          ${step(2, 'Describe your scene')}
          ${sceneHtml()}
          ${step(3, 'Video duration')}
          ${durationHtml()}
          ${step(4, 'Quality')}
          ${qualityHtml()}
          <footer class="i2v-foot" id="i2v-foot">${footerHtml()}</footer>
          <input type="file" id="ing-file" accept="${ACCEPTED_TYPES.join(',')}" multiple hidden />
        </div>`;
      const ta = root.querySelector<HTMLTextAreaElement>('#ing-prompt');
      if (ta) ta.value = prompt;
      syncPromptUi();
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#i2v-foot');
      if (f) f.innerHTML = footerHtml();
    };

    function syncPromptUi(): void {
      const count = root.querySelector<HTMLElement>('#ing-count');
      if (count) count.textContent = `${prompt.length}/${PROMPT_MAX}`;
      const clear = root.querySelector<HTMLElement>('#ing-clear');
      if (clear) clear.hidden = prompt.length === 0;
    }

    // -- Upload -----------------------------------------------------------

    const uploadOne = async (file: File): Promise<void> => {
      const slot: Slot = { key: ++slotSeq, status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, error: null };
      slots.push(slot);
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || !slots.includes(slot)) return;
          slot.progress = r;
          const bar = root.querySelector<HTMLElement>(`[data-key="${slot.key}"] .i2v-bar span`);
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>(`[data-key="${slot.key}"] [data-slot-label]`);
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || !slots.includes(slot)) {
          if (meta.id) void api.deleteUpload(meta.id).catch(() => undefined);
          return;
        }
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        slot.status = 'ready';
        slot.fileId = meta.id;
        slot.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? slot.previewUrl;
      } catch (err) {
        if (!alive() || !slots.includes(slot)) return;
        slot.status = 'error';
        slot.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    const addFiles = (files: File[]): void => {
      if (!requireSignIn('Sign in to add your photos.')) return;
      const room = MAX_IMAGES - slots.length;
      if (room <= 0) return;
      const picked = files.filter((f) => ACCEPTED_TYPES.includes(f.type) && f.size <= CLIENT_MAX_BYTES);
      if (picked.length < files.length) toast('Some images were skipped. Use JPEG, PNG or WebP under 25 MB.');
      if (picked.length > room) toast(`You can add up to ${MAX_IMAGES} images.`);
      for (const f of picked.slice(0, room)) void uploadOne(f);
    };

    const removeSlot = (key: number): void => {
      const s = slots.find((x) => x.key === key);
      if (!s) return;
      slots = slots.filter((x) => x !== s);
      if (s.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(s.previewUrl);
      paint();
      if (s.fileId) void api.deleteUpload(s.fileId).catch(() => undefined);
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = cost();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const options: JobOptions | undefined = style ? { sceneStyle: style } : undefined;
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: slots.filter((s) => s.status === 'ready' && s.fileId).map((s) => s.fileId as string),
          prompt: prompt.trim(),
          ...(options ? { options } : {}),
          qualityTier: tier,
          durationSec: duration,
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-act],[data-remove],[data-style],[data-duration],[data-tier]',
      );
      if (!el) return;
      const d = el.dataset;
      if (d.remove) removeSlot(Number(d.remove));
      else if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photos.')) withUploadGuide('ingredients', () => root.querySelector<HTMLInputElement>('#ing-file')?.click());
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.act === 'clear') {
        prompt = '';
        const ta = root.querySelector<HTMLTextAreaElement>('#ing-prompt');
        if (ta) {
          ta.value = '';
          ta.focus();
        }
        syncPromptUi();
        paintFooter();
      } else if (d.style) {
        const next = STYLES.find((s) => s.id === d.style)?.id ?? null;
        style = style === next ? null : next;
        root.querySelectorAll<HTMLElement>('[data-style]').forEach((b) => {
          const on = b.dataset.style === style;
          b.classList.toggle('selected', on);
          b.setAttribute('aria-pressed', String(on));
        });
      } else if (d.duration) {
        duration = Number(d.duration) as VideoDurationSec;
        paint();
      } else if (d.tier) {
        tier = d.tier as QualityTier;
        paint();
      }
    };

    root.oninput = (e) => {
      const ta = e.target as HTMLElement;
      if (ta.id !== 'ing-prompt') return;
      prompt = (ta as HTMLTextAreaElement).value;
      syncPromptUi();
      paintFooter();
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'ing-file') return;
      const files = Array.from(input.files ?? []);
      input.value = '';
      if (files.length) addFiles(files);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      for (const s of slots) if (s.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(s.previewUrl);
    };

    paint();
  };

  void load();
}
